import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  InputText,
  SectionCard,
  Select,
  type ColumnDef,
} from "@/deps/ui-library";
import {
  useAccounts,
  useBankAccounts,
  usePaymentOptions,
  useRemovePaymentOption,
  useSavePaymentOption,
} from "@/api/queries";
import type { PaymentOption, PaymentOptionKind } from "@/api/types";
import { Dialog } from "@/components/Dialog";

type Editing = {
  id?: string;
  kind: PaymentOptionKind;
  bankAccountId: string;
  name: string;
  payee: string;
  shownOnInvoice: boolean;
};

const KINDS: Array<{ value: PaymentOptionKind; label: string }> = [
  { value: "bank", label: "Bank transfer" },
  { value: "wallet", label: "Wallet" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
];

const kindLabel = (kind: PaymentOptionKind) => KINDS.find((k) => k.value === kind)?.label ?? kind;

/** What a person calls the option, which is what tells two of a kind apart. */
function optionName(option: PaymentOption): string {
  return option.kind === "wallet" ? (option.name ?? "") : option.bankAccountName;
}

function optionMeta(option: PaymentOption): string {
  if (option.kind === "wallet") return `Deposited into ${option.bankAccountName}`;
  if (option.kind === "cheque") return `Payable to ${option.payee ?? ""}`;
  if (option.kind === "bank") return option.accountNumber ? `A/C ${option.accountNumber}` : "";
  return "";
}

/**
 * How customers can pay. Each option names one of the company's bank or cash accounts, and an invoice
 * copies the wording when it's saved, so a change here doesn't alter one already issued.
 */
export function PaymentOptionsCard() {
  const { data: options } = usePaymentOptions();
  const { data: chart } = useAccounts();
  const save = useSavePaymentOption();
  const remove = useRemovePaymentOption();
  const [editing, setEditing] = useState<Editing | null>(null);

  const ledgerName = (id: string) => {
    const account = chart?.find((a) => a.id === id);
    return account ? `${account.code} · ${account.name}` : "";
  };

  const columns: Array<ColumnDef<PaymentOption>> = [
    {
      id: "kind",
      header: "Method",
      cell: ({ row: { original: option } }) => kindLabel(option.kind),
    },
    {
      id: "account",
      header: "Account",
      cell: ({ row: { original: option } }) => (
        <span className="flex flex-col">
          <span>{optionName(option)}</span>
          <span className="text-fg-muted text-xs">{optionMeta(option)}</span>
        </span>
      ),
    },
    {
      id: "ledger",
      header: "Posts to",
      cell: ({ row: { original: option } }) => ledgerName(option.ledgerAccountId),
    },
    {
      id: "shown",
      header: "On invoice",
      cell: ({ row: { original: option } }) => (
        <Checkbox
          aria-label={`Show ${optionName(option)} on invoices`}
          checked={option.shownOnInvoice}
          disabled={save.isPending}
          onCheckedChange={(details) =>
            save.mutate({ ...option, shownOnInvoice: details.checked === true })
          }
        />
      ),
    },
    {
      id: "action",
      header: "",
      cell: ({ row: { original: option } }) => (
        <span className="flex justify-end gap-1">
          <Button
            colorScheme="default"
            variant="ghost"
            size="xs"
            aria-label={`Edit ${optionName(option)}`}
            onClick={() =>
              setEditing({
                id: option.id,
                kind: option.kind,
                bankAccountId: option.bankAccountId,
                name: option.name ?? "",
                payee: option.payee ?? "",
                shownOnInvoice: option.shownOnInvoice,
              })
            }
          >
            Edit
          </Button>
          <Button
            colorScheme="default"
            variant="ghost"
            size="xs"
            disabled={remove.isPending}
            aria-label={`Remove ${optionName(option)}`}
            onClick={() => remove.mutate(option.id)}
          >
            Remove
          </Button>
        </span>
      ),
    },
  ];

  const error = save.error ?? remove.error;

  return (
    <SectionCard
      title="Payment options"
      actions={
        <Button
          colorScheme="default"
          variant="outline"
          size="sm"
          onClick={() =>
            setEditing({
              kind: "bank",
              bankAccountId: "",
              name: "",
              payee: "",
              shownOnInvoice: true,
            })
          }
        >
          New payment option
        </Button>
      }
      flush
    >
      {error && editing === null ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}
      <DataTable
        aria-label="Payment options"
        size="sm"
        enableSorting={false}
        columns={columns}
        data={options ?? []}
        getRowId={(option) => option.id}
        loading={options === undefined}
        emptyContent="No payment options yet. Invoices print without payment details until one is added."
        pagination={{ mode: "manual", show: false }}
      />
      {editing ? <OptionDialog start={editing} onClose={() => setEditing(null)} /> : null}
    </SectionCard>
  );
}

function OptionDialog({ start, onClose }: { start: Editing; onClose: () => void }) {
  const { data: accounts } = useBankAccounts();
  const save = useSavePaymentOption();
  const [form, setForm] = useState(start);

  // Only the kinds that have one send a name or a payee.
  const account = accounts?.find((a) => a.id === form.bankAccountId) ?? accounts?.[0];
  const complete =
    account !== undefined &&
    (form.kind !== "wallet" || form.name.trim() !== "") &&
    (form.kind !== "cheque" || form.payee.trim() !== "");

  return (
    <Dialog
      open
      onClose={onClose}
      title={start.id ? "Edit payment option" : "New payment option"}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={!complete || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: form.id,
                kind: form.kind,
                bankAccountId: account!.id,
                name: form.kind === "wallet" ? form.name.trim() : undefined,
                payee: form.kind === "cheque" ? form.payee.trim() : undefined,
                shownOnInvoice: form.shownOnInvoice,
              },
              { onSuccess: onClose },
            )
          }
        >
          {save.isPending ? "Saving" : "Save"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <Select
          label="Method"
          required
          options={KINDS}
          value={form.kind}
          showClearButton={false}
          onChange={(details) => {
            const option = Array.isArray(details.value) ? details.value[0] : details.value;
            if (option) setForm({ ...form, kind: String(option.value) as PaymentOptionKind });
          }}
        />
        {form.kind === "wallet" ? (
          <InputText
            label="Wallet name"
            required
            placeholder="e.g. eSewa"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        ) : null}
        <Select
          label={form.kind === "wallet" || form.kind === "cheque" ? "Deposited into" : "Account"}
          required
          options={(accounts ?? []).map((a) => ({ value: a.id, label: a.name }))}
          value={account?.id ?? ""}
          showClearButton={false}
          onChange={(details) => {
            const option = Array.isArray(details.value) ? details.value[0] : details.value;
            if (option) setForm({ ...form, bankAccountId: String(option.value) });
          }}
        />
        {form.kind === "cheque" ? (
          <InputText
            label="Payable to"
            required
            value={form.payee}
            onChange={(event) => setForm({ ...form, payee: event.target.value })}
          />
        ) : null}
        <Checkbox
          label="Show on invoices"
          checked={form.shownOnInvoice}
          onCheckedChange={(details) =>
            setForm({ ...form, shownOnInvoice: details.checked === true })
          }
        />
      </div>
    </Dialog>
  );
}
